import { Injectable } from '@angular/core';
import { EMPTY, Observable, of, map, switchMap, tap, catchError } from 'rxjs';

import { DemandsService } from '../../authentication/services/demands.service';
import { BillingCheckoutResponse } from '../../organizations/models/organizations.entity';
import { OrganizationRepository } from '../../organizations/repositories/organization.repository';
import { JourneyCheckoutSelectorDialogComponent } from '../components/journey-checkout-selector-dialog/journey-checkout-selector-dialog.component';
import { DialogService } from '../components/dialog/dialog.service';
import { DynamicType } from '../../thematics/models/dynamic.model';
import { Talker } from '../../users/models/talker.entity';
import { ProfileService } from '../../users/services/profile.service';
import { SnackbarService } from '../components/snackbar/snackbar.service';
import { specialSubscriptionPlans } from '../tool-functions/constants';
import { getRequiredCredits } from '../tool-functions/credit.constants';

export interface CreditCheckResult {
  canProceed: boolean;
  reason?: string;
  noUnitAvailable?: boolean;
  isSpecialPlan?: boolean;
  alreadyAskedForMoreUnits?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class CreditCheckService {
  
  constructor(
    private organizationRepository: OrganizationRepository,
    private snackBarService: SnackbarService,
    private demandService: DemandsService,
    private profileService: ProfileService,
    private dialogService: DialogService,
  ) {}

  getRequiredUnits(trainType: DynamicType): number {
    return getRequiredCredits(trainType);
  }

  checkCredits(trainType: DynamicType): Observable<CreditCheckResult> {
    return this.organizationRepository.canConsumeUnit().pipe(
      map(can => {
        const requiredUnits = getRequiredCredits(trainType, can.creditCosts);
        const isSpecialPlan = specialSubscriptionPlans.includes(can.subscriptionPlanSlug);
        
        if (can.unitAvailable && can.quota) {
          if (can.units >= requiredUnits) {
            return { canProceed: true };
          } 
          const message = isSpecialPlan
            ? `Vous n'avez pas assez de crédits pour réserver ce parcours.`
            : `Votre entreprise n'a pas assez de crédits pour réserver ce parcours.`;
            
          return { 
            canProceed: false, 
            reason: message, 
            noUnitAvailable: true,
            isSpecialPlan,
            alreadyAskedForMoreUnits: can.alreadyAskForMoreUnits
          };
        } 
        
        const message = isSpecialPlan
          ? (!can.unitAvailable 
              ? `Vous n'avez pas assez de crédits pour réserver ce parcours.` 
              : `Vous avez consommé le quota de crédits qui vous a été accordé.`)
          : (!can.unitAvailable 
              ? `Votre entreprise n'a pas assez de crédits pour réserver ce parcours.` 
              : `Vous avez consommé le quota de crédits qui vous a été accordé par votre entreprise.`);
        
        return { 
          canProceed: false, 
          reason: message,
          noUnitAvailable: !can.unitAvailable,
          isSpecialPlan,
          alreadyAskedForMoreUnits: can.alreadyAskForMoreUnits
        };
      })
    );
  }
  
  askForMoreUnits(trainType?: DynamicType): Observable<void> {
    const organizationId = (this.profileService.profile as Talker)?.organization?.id || "";
    
    if (this.isSpecialPlan()) {
      return this.startJourneyCheckout(organizationId, trainType);
    }
    
    return this.demandService.askForMoreUnits(organizationId);
  }
  
  private isSpecialPlan(): boolean {
    const subscriptionPlanSlug = (this.profileService.profile as Talker)?.organization?.subscriptionPlan?.slug || "";
    return specialSubscriptionPlans.includes(subscriptionPlanSlug);
  }

  checkAndNotify(trainType: DynamicType, onSuccess: () => void, onInsufficientCredits?: () => void): void {
    this.checkCredits(trainType).subscribe(result => {
      if (result.canProceed) {
        onSuccess();
      } else {
        if (result.reason) {
          this.snackBarService.pushMessage(result.reason, "error");
        }
        
        if (result.noUnitAvailable && !result.alreadyAskedForMoreUnits) {
          this.askForMoreUnits(trainType).subscribe(() => {
            if (onInsufficientCredits) {
              onInsufficientCredits();
            }
          });
        } else if (result.noUnitAvailable && onInsufficientCredits) {
          onInsufficientCredits();
        }
      }
    });
  }

  private startJourneyCheckout(
    organizationId: string,
    trainType?: DynamicType,
  ): Observable<void> {
    if (!organizationId) {
      this.snackBarService.pushMessage("Impossible de préparer l'achat du parcours", "error");
      return EMPTY;
    }

    const journeyType$: Observable<DynamicType | undefined> = trainType
      ? of(trainType)
      : this.dialogService.open(
        JourneyCheckoutSelectorDialogComponent,
        {
          title: "Acheter un parcours",
          message: "Choisissez le type de parcours à régler dans Sellsy.",
        },
      );

    return journeyType$.pipe(
      switchMap((journeyType) => {
        if (!journeyType) {
          return EMPTY;
        }

        return this.organizationRepository.createComplementaryJourneysCheckout(organizationId, {
          quantity: 1,
          journeyType,
        });
      }),
      tap((response: BillingCheckoutResponse) => {
        if (response.checkoutUrl) {
          window.open(response.checkoutUrl, '_blank');
          this.snackBarService.pushMessage("Le lien de paiement Sellsy a été ouvert", "success");
          return;
        }

        this.snackBarService.pushMessage("Le checkout Sellsy n'a pas retourné de lien de paiement", "error");
      }),
      map(() => void 0),
      catchError(() => {
        this.snackBarService.pushMessage("Le checkout Sellsy du parcours a échoué", "error");
        return EMPTY;
      }),
    );
  }
}
